// ---------------------------------------------------------------------------
// THE BUTTON SHAPES (DESIGN.md §17), as sx on a MUI Button so the interaction
// stays MUI's: primary (the accent fill), secondary (a hairline), ghost (text
// in the accent), and the icon button. No shadow, no uppercase, no gradient;
// the pressed state is the fill step; the focus ring is the accent ring. The
// visual size stays 32 / 36 / 40; on touch an invisible 44px hit area wraps it.
// ---------------------------------------------------------------------------

import { alpha } from '@mui/material/styles';
import {
  MOTION,
  RADIUS,
  UNIVERSE_FONT,
  accentOf,
  accentTint,
  fieldLineOf,
  fillHoverOf,
  fillOf,
  fillPressedOf,
  focusRingSx,
  onFillOf,
  touchHitSx
} from './brand';
import { toneInkOf, toneTintOf } from './tones';

// A pill (RADIUS.button, 08/10/2026) wants a little more room at its ends
// than a 6px box did: 12 / 16px instead of 10 / 14.
const base = (theme, size) => ({
  minHeight: size === 'small' ? 32 : 36,
  px: size === 'small' ? 1.5 : 2,
  borderRadius: `${RADIUS.button}px`,
  fontFamily: UNIVERSE_FONT,
  fontWeight: 600,
  fontSize: size === 'small' ? 13 : 14,
  lineHeight: 1,
  letterSpacing: 0,
  textTransform: 'none',
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  // The hover is a felt step, not a shade (the owner, 07/10/2026: the hover
  // "needs to be improved big times"): the fill steps, the button lifts a
  // pixel and casts a soft glow in its own colour; pressing drops it back.
  transition: theme.transitions.create(
    ['background-color', 'color', 'border-color', 'transform', 'box-shadow'],
    {
      duration: MOTION.standard,
      easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)'
    }
  ),
  '& .MuiButton-startIcon': { mr: 1, ml: -0.25 },
  '& .MuiButton-endIcon': { ml: 1, mr: -0.25 },
  '&:hover': { boxShadow: 'none' },
  '&:focus-visible': focusRingSx(theme),
  '&.Mui-disabled': { opacity: 0.45, transform: 'none', boxShadow: 'none' },
  '@media (pointer: coarse)': touchHitSx,
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:hover': { transform: 'none' }
  }
});

export const primaryButtonSx = (theme, size) => ({
  ...base(theme, size),
  color: onFillOf(theme),
  bgcolor: fillOf(theme),
  '&:hover': {
    bgcolor: fillHoverOf(theme),
    transform: 'translateY(-1px)',
    boxShadow: `0 8px 18px -8px ${alpha(fillOf(theme), 0.6)}`
  },
  '&:active': {
    bgcolor: fillPressedOf(theme),
    transform: 'translateY(0)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,.2)'
  },
  '&.Mui-disabled': {
    color: onFillOf(theme),
    bgcolor: fillOf(theme),
    opacity: 0.45
  }
});

// A secondary button wears the field line, not the darker control line, and
// its glyph sits a step quieter than its word (the owner, 07/10/2026: the
// Columns and Export buttons "can look better").
export const secondaryButtonSx = (theme, size) => ({
  ...base(theme, size),
  color: theme.palette.text.primary,
  bgcolor: 'background.paper',
  border: `1px solid ${fieldLineOf(theme)}`,
  '& .MuiButton-startIcon': {
    mr: 1,
    ml: -0.25,
    color: theme.palette.text.secondary,
    transition: `color ${MOTION.standard}ms`
  },
  '&:hover': {
    color: accentOf(theme),
    bgcolor: accentTint(theme, 'hover'),
    borderColor: alpha(accentOf(theme), 0.5),
    transform: 'translateY(-1px)',
    boxShadow: `0 6px 14px -8px ${alpha(accentOf(theme), 0.35)}`,
    '& .MuiButton-startIcon': { color: accentOf(theme) }
  },
  '&:active': {
    bgcolor: accentTint(theme, 'selected'),
    transform: 'translateY(0)',
    boxShadow: 'none'
  }
});

export const ghostButtonSx = (theme, size) => ({
  ...base(theme, size),
  color: accentOf(theme),
  bgcolor: 'transparent',
  '&:hover': { bgcolor: accentTint(theme, 'hover'), boxShadow: 'none' },
  '&:active': { bgcolor: accentTint(theme, 'selected') }
});

/** A secondary button that is "on": the accent tint and the accent ink. */
export const softButtonSx = (theme, size) => ({
  ...base(theme, size),
  color: accentOf(theme),
  bgcolor: accentTint(theme, 'soft'),
  border: `1px solid ${alpha(accentOf(theme), 0.32)}`,
  '&:hover': { bgcolor: accentTint(theme, 'selected'), boxShadow: 'none' }
});

/** The icon button: 32 / 36 round, ghost by default, a 44px hit area on touch. */
export const iconButtonSx = (theme, size = 36) => ({
  width: size,
  height: size,
  p: 0,
  borderRadius: `${RADIUS.button}px`,
  color: theme.palette.text.secondary,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: MOTION.fast
  }),
  '&:hover': { color: accentOf(theme), bgcolor: accentTint(theme, 'hover') },
  '&:active': { bgcolor: accentTint(theme, 'selected') },
  '&:focus-visible': focusRingSx(theme),
  '@media (pointer: coarse)': touchHitSx
});

/**
 * A control that is also a state (DESIGN.md §17 "tone"): the tone's wash
 * and line with the word in the tone's ink — the request buttons of a
 * staffing step (the accent until sent, amber while waiting, green once
 * answered). `default` is the soft accent button, so a plain action is
 * unchanged. The callers change the label too: the colour is never the only
 * signal.
 */
export const toneButtonSx = (theme, tone = 'default', size) => {
  const key = !tone || tone === 'default' || tone === 'accent' ? 'accent' : tone;
  const ink = key === 'accent' ? accentOf(theme) : toneInkOf(theme, key);
  return {
    ...base(theme, size),
    color: ink,
    bgcolor: toneTintOf(theme, key, 'label'),
    boxShadow: `inset 0 0 0 1px ${toneTintOf(theme, key, 'line')}`,
    '&:hover': {
      bgcolor: toneTintOf(theme, key, 'rest'),
      boxShadow: `inset 0 0 0 1px ${toneTintOf(theme, key, 'line')}`
    },
    '&:active': { bgcolor: toneTintOf(theme, key, 'hover') },
    '&.Mui-disabled': { color: ink, opacity: 0.45 }
  };
};

/**
 * The destructive button (DESIGN.md §17): the error tone's mark as the
 * fill with white on it on paper; on the night the error ink with the dark
 * ground's ink on it. Delete, remove, refuse, cancel a project.
 */
export const destructiveButtonSx = (theme, size) => {
  const light = theme.palette.mode === 'light';
  const fill = toneInkOf(theme, 'error');
  const onFill = light ? '#FFFFFF' : '#111B33';
  return {
    ...base(theme, size),
    color: onFill,
    bgcolor: fill,
    '&:hover': {
      bgcolor: fill,
      filter: light ? 'brightness(0.92)' : 'brightness(1.08)',
      transform: 'translateY(-1px)',
      boxShadow: `0 8px 18px -8px ${alpha(fill, 0.6)}`
    },
    '&:active': {
      transform: 'translateY(0)',
      filter: 'none',
      boxShadow: 'inset 0 1px 2px rgba(0,0,0,.2)'
    },
    '&.Mui-disabled': { color: onFill, bgcolor: fill, opacity: 0.45 }
  };
};
